import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Search, ArrowUpDown, BarChart3 } from 'lucide-react';
import Card from '../common/Card';
import StatusIndicator, { getStockLevel, getStockLevelColor } from '../common/StatusIndicator';
import { useApi } from '../../hooks/useApi';
import { getZones, getStockHealth } from '../../api/client';

const ZONES = [
  'RASMUSTAV', 'Clear_Sky', 'SIDOROVICH', 'Bradislav',
  'Wirpmenistan', 'Briggosia', 'GREENMOUNTAIN',
];

const SORT_OPTIONS = [
  { label: 'Name', value: 'name' },
  { label: 'Stock Level', value: 'stock' },
  { label: 'Activity', value: 'activity' },
];

export default function ZoneList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');

  const { data: zonesData, loading: zonesLoading } = useApi(getZones);
  const { data: healthData, loading: healthLoading } = useApi(getStockHealth);

  const loading = zonesLoading || healthLoading;

  const zones = useMemo(() => {
    const healthMap = {};
    if (healthData) {
      const arr = Array.isArray(healthData) ? healthData : healthData.zones || [];
      arr.forEach((z) => {
        healthMap[z.zone || z.name] = z;
      });
    }

    const zonesArr = Array.isArray(zonesData) ? zonesData : zonesData?.zones || [];
    const zoneMap = {};
    zonesArr.forEach((z) => {
      zoneMap[z.zone || z.name] = z;
    });

    let result = ZONES.map((name) => {
      const z = zoneMap[name] || {};
      const h = healthMap[name] || {};
      return {
        name,
        stockPercent: h.stockPercent ?? h.healthPercent ?? h.stock_percent ?? 50,
        totalItems: h.totalItems ?? h.total_items ?? z.totalItems ?? 0,
        lowStockItems: h.lowStockItems ?? h.low_stock_items ?? 0,
        transactions: z.transactions ?? z.totalTransactions ?? 0,
        categories: z.categories ?? 0,
        description: z.description || '',
      };
    });

    // Filter
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((z) => z.name.toLowerCase().includes(q));
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'stock') return b.stockPercent - a.stockPercent;
      if (sortBy === 'activity') return b.transactions - a.transactions;
      return a.name.localeCompare(b.name);
    });

    return result;
  }, [zonesData, healthData, search, sortBy]);

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <MapPin className="w-5 h-5 text-accent-green" />
          Trader Zones
        </h1>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search zones..."
              className="bg-terminal-bg border border-terminal-border rounded pl-8 pr-3 py-1.5 text-xs text-text-primary font-mono placeholder:text-text-muted focus:outline-none focus:border-accent-green/50 w-48"
            />
          </div>

          {/* Sort */}
          <div className="flex items-center gap-1">
            <ArrowUpDown className="w-3.5 h-3.5 text-text-muted" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-terminal-bg border border-terminal-border rounded px-2 py-1.5 text-xs text-text-secondary font-mono focus:outline-none focus:border-accent-green/50"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="skeleton h-40 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {zones.map((zone) => {
            const color = getStockLevelColor(zone.stockPercent);

            return (
              <div
                key={zone.name}
                onClick={() => navigate(`/zones/${encodeURIComponent(zone.name)}`)}
                className="glass-card p-5 cursor-pointer hover:border-text-muted transition-all duration-200 group"
                style={{ borderTopColor: color, borderTopWidth: '2px' }}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-mono font-bold text-text-primary group-hover:text-accent-green transition-colors">
                    {zone.name}
                  </h3>
                  <StatusIndicator type="stock" value={zone.stockPercent} />
                </div>

                {/* Progress Bar */}
                <div className="w-full h-2 bg-terminal-bg rounded-full mb-4 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, zone.stockPercent)}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-[10px] text-text-muted uppercase">Stock</p>
                    <p className="text-sm font-mono font-bold" style={{ color }}>
                      {zone.stockPercent.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-text-muted uppercase">Items</p>
                    <p className="text-sm font-mono font-bold text-text-primary">
                      {zone.totalItems.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-text-muted uppercase">Low Stock</p>
                    <p className={`text-sm font-mono font-bold ${zone.lowStockItems > 0 ? 'text-accent-amber' : 'text-text-muted'}`}>
                      {zone.lowStockItems}
                    </p>
                  </div>
                </div>

                {zone.transactions > 0 && (
                  <div className="mt-3 pt-3 border-t border-terminal-border flex items-center gap-1">
                    <BarChart3 className="w-3 h-3 text-text-muted" />
                    <span className="text-[10px] text-text-muted font-mono">
                      {zone.transactions.toLocaleString()} transactions
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
